/*
 * Ryan O'Dowd
 * 2025-09-11
 *
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
*/
import React, {
  createContext,
  useContext,
  useState,
} from 'react';
import routing from '@utils/routing';

type HeaderHeights = Record<string, number>;

interface HeaderHeightContextValue {
  heights: HeaderHeights;
  setHeight: (routeKey: string, height: number) => void;
}

const HeaderHeightContext = createContext<HeaderHeightContextValue | undefined>(undefined);

export const HeaderHeightProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [_heights, setHeights] = useState<HeaderHeights>({});

  const setHeight = (routeKey: string, height: number) => {
    setHeights((prev) => ({...prev, [routeKey]: height}));
  };

  return (
    <HeaderHeightContext.Provider value={{heights: _heights, setHeight}}>
      {children}
    </HeaderHeightContext.Provider>
  );
};

// Hook to get the header height for the current route
export function useHeaderHeight(): number {
  const context = useContext(HeaderHeightContext);
  if (!context) throw new Error('useHeaderHeight must be used within HeaderHeightProvider');

  const route: any = routing.useRoute();
  return context.heights[route.key] ?? 0;
}

// Hook to set the header height for the current route
export function useSetHeaderHeight() {
  const context = useContext(HeaderHeightContext);
  if (!context) throw new Error('useSetHeaderHeight must be used within HeaderHeightProvider');

  const route: any = routing.useRoute();
  return (height: number) => context.setHeight(route.key, height);
}
